/**
 * DocumentsHub — central registry for every document across the holding
 * company. Each row shows:
 *
 *   • document kind + filename
 *   • parent entity / startup / agreement
 *   • upload date
 *   • OCR status (pending / done / failed) + extracted expiry chip
 *   • version chip ("v3" if it was replaced twice; default v1)
 *
 * Actions: upload (drag-drop or button), replace (uploads a new version),
 * retry OCR, view the full chain, manually override expiry, soft-delete.
 *
 * Powered by the Gemini Vision OCR pipeline (free via the platform LLM key):
 * every PDF/PNG/JPG is read automatically and key fields (expiry, doc
 * number, parties) are extracted — feeding the daily expiry scanner so
 * nothing falls through the cracks.
 */
import React, { useEffect, useRef, useState } from 'react';
import {
  Upload, FileText, History, RefreshCcw, AlertTriangle, CheckCircle2,
  Clock, Loader2, ShieldCheck, Search, X, Eye, Download,
} from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

const KIND_LABELS = {
  cr_copy: 'Commercial Registration',
  baladiya_card: 'Baladiya Card',
  tax_card: 'Tax Card',
  moa: 'MOA',
  aoa: 'AOA',
  financial_statement: 'Financial Statement',
  audit_report: 'Audit Report',
  tax_filing: 'Tax Filing',
  agreement: 'Agreement',
  board_resolution: 'Board Resolution',
  minutes: 'Board Minutes',
  license: 'License',
  due_diligence: 'Due-Diligence Doc',
  pitch_deck: 'Pitch Deck',
  other: 'Other',
};

function daysFromNow(iso) {
  if (!iso) return null;
  try {
    const target = new Date(iso).getTime();
    if (Number.isNaN(target)) return null;
    return Math.round((target - Date.now()) / 86400000);
  } catch { return null; }
}

function ExpiryChip({ iso }) {
  const d = daysFromNow(iso);
  if (d === null) return <span className="text-[10px] text-slate-400 dark:text-white/35">—</span>;
  const tone =
    d < 0 ? 'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300' :
    d <= 7 ? 'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300' :
    d <= 30 ? 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300' :
    d <= 60 ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-500/15 dark:text-yellow-300' :
    'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300';
  const text = d < 0 ? `Expired ${Math.abs(d)}d ago` : d === 0 ? 'Today' : `${d}d`;
  return <span className={`inline-flex items-center gap-1 px-2 h-5 rounded text-[10px] font-bold ${tone}`}><Clock size={10}/>{text}</span>;
}

function OcrStatusChip({ status, confidence }) {
  if (status === 'done') return (
    <span className="inline-flex items-center gap-1 px-2 h-5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300">
      <CheckCircle2 size={10}/> OCR {confidence ?? ''}%
    </span>
  );
  if (status === 'pending') return (
    <span className="inline-flex items-center gap-1 px-2 h-5 rounded text-[10px] font-bold bg-slate-100 text-slate-600 dark:bg-white/10 dark:text-white/55">
      <Loader2 size={10} className="animate-spin"/> OCR…
    </span>
  );
  if (status === 'failed') return (
    <span className="inline-flex items-center gap-1 px-2 h-5 rounded text-[10px] font-bold bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300">
      <AlertTriangle size={10}/> Failed
    </span>
  );
  return <span className="text-[10px] text-slate-400 dark:text-white/35">—</span>;
}

export default function DocumentsHub() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState('');
  const [kindFilter, setKindFilter] = useState('all');
  const [pickedKind, setPickedKind] = useState('other');
  const [versionsModal, setVersionsModal] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  // Per-file upload state: [{ id, name, size, status, error }]
  const [queue, setQueue] = useState([]);
  const dropRef = useRef(null);
  // Auto-refresh poller state — bounded so we never poll forever if a
  // doc gets stuck in `pending` (e.g. Gemini timeout).
  const pollAttemptsRef = useRef(0);
  const MAX_POLL_ATTEMPTS = 20; // ≈ 80s of polling after upload

  // `silent=true` skips the loading spinner so auto-refresh polls don't
  // wipe the table and reset the scroll position.
  const load = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get('/documents/?current_only=true');
      setDocs(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load documents.');
    } finally { if (!silent) setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  // Auto-refresh while ANY document is still pending OCR. Bounded by
  // MAX_POLL_ATTEMPTS so a stuck row can't drive the loop forever, and
  // uses `silent: true` so the table doesn't blank between polls.
  useEffect(() => {
    const anyPending = docs.some((d) => d.ocr_status === 'pending');
    if (!anyPending) {
      pollAttemptsRef.current = 0;
      return undefined;
    }
    if (pollAttemptsRef.current >= MAX_POLL_ATTEMPTS) return undefined;
    const t = setInterval(() => {
      pollAttemptsRef.current += 1;
      load({ silent: true });
    }, 4000);
    return () => clearInterval(t);
  }, [docs]);

  const filtered = docs.filter((d) => {
    if (kindFilter !== 'all' && d.kind !== kindFilter) return false;
    if (!q) return true;
    const hay = `${d.original_filename || ''} ${d.kind || ''} ${d.doc_number_extracted || ''} ${d.issuing_authority_extracted || ''}`.toLowerCase();
    return hay.includes(q.toLowerCase());
  });

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('kind', pickedKind);
    try {
      await api.post('/documents/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`${file.name} uploaded — OCR running in the background.`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed.');
    } finally { setBusy(false); }
  };

  // ── Multi-file drag-and-drop ────────────────────────────────────────
  // Uploads up to 3 files concurrently to keep the OCR queue healthy.
  const uploadOne = async (qItem) => {
    setQueue((q) => q.map((x) => x.id === qItem.id ? { ...x, status: 'uploading' } : x));
    const fd = new FormData();
    fd.append('file', qItem.file);
    fd.append('kind', pickedKind);
    try {
      await api.post('/documents/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setQueue((q) => q.map((x) => x.id === qItem.id ? { ...x, status: 'done' } : x));
    } catch (e) {
      setQueue((q) => q.map((x) => x.id === qItem.id ? {
        ...x, status: 'failed', error: e?.response?.data?.detail || 'Upload failed',
      } : x));
    }
  };

  const uploadMany = async (fileList) => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (!files.length) return;
    const items = files.map((f) => ({
      id: `${f.name}-${f.size}-${f.lastModified}-${Math.random().toString(36).slice(2,7)}`,
      file: f, name: f.name, size: f.size, status: 'queued',
    }));
    setQueue((q) => [...q, ...items]);
    // Concurrency=3 pool
    let cursor = 0;
    const worker = async () => {
      while (cursor < items.length) {
        const next = items[cursor++];
        await uploadOne(next);
      }
    };
    await Promise.all([worker(), worker(), worker()]);
    toast.success(`${files.length} file${files.length === 1 ? '' : 's'} uploaded — OCR running.`);
    // Reset the polling counter so the auto-refresh re-arms for the new uploads.
    pollAttemptsRef.current = 0;
    await load();
    // Auto-clear the queue after 4s so the UI doesn't grow forever
    setTimeout(() => setQueue((q) => q.filter((x) => x.status !== 'done')), 4000);
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    uploadMany(e.dataTransfer?.files);
  };

  const onDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  };

  const replace = async (docId, file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      await api.post(`/documents/${docId}/replace`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('New version uploaded. Older version archived.');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Replace failed.');
    } finally { setBusy(false); }
  };

  const retryOcr = async (docId) => {
    setBusy(true);
    try {
      await api.post(`/documents/${docId}/ocr/retry`);
      toast.success('OCR re-run complete.');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'OCR retry failed.');
    } finally { setBusy(false); }
  };

  // Fetch the document as a blob (auth header is required) and open it
  // in a new tab. We deliberately don't generate a presigned URL — that
  // would let anyone with the link bypass the auth gate.
  const viewDoc = async (docId, action = 'view') => {
    try {
      const resp = await api.get(`/documents/${docId}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(resp.data);
      if (action === 'download') {
        const a = document.createElement('a');
        a.href = url;
        // try to pull the filename from headers; fall back to docId
        const cd = resp.headers['content-disposition'] || '';
        const m = cd.match(/filename="?([^"]+)"?/);
        a.download = m ? m[1] : `${docId}`;
        a.click();
      } else {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not open this document.');
    }
  };

  const showVersions = async (docId) => {
    try {
      const { data } = await api.get(`/documents/${docId}/versions`);
      setVersionsModal(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load versions.');
    }
  };

  return (
    <div className="ve-section" data-testid="documents-hub">
      <div className="ve-eyebrow"><FileText size={11}/> Document Management</div>
      <div className="flex items-start justify-between gap-6 mt-2">
        <div>
          <h1 className="ve-h2 mt-2 text-slate-900 dark:text-white">
            Every document. <span className="ve-gold-text">OCR&rsquo;d. Versioned. Watched.</span>
          </h1>
          <p className="text-sm text-slate-600 dark:text-white/70 mt-2 max-w-2xl">
            Gemini Vision reads every PDF and image on upload — extracting expiry dates,
            document numbers and parties in Arabic + English. Older versions are kept
            for full audit. Daily scan @ 08:00 UTC alerts the council before anything lapses.
          </p>
        </div>
        <div className="hidden lg:flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] font-bold text-[#5E1F8B] dark:text-[#8B3FC5]">
          <ShieldCheck size={12}/> AES-256-GCM at rest
        </div>
      </div>

      {/* Upload + filters */}
      <div className="mt-8 grid gap-4 lg:grid-cols-[1fr_auto] items-start">
        <div
          ref={dropRef}
          onDragEnter={onDrag} onDragOver={onDrag} onDragLeave={onDrag}
          onDrop={onDrop}
          data-testid="docs-dropzone"
          className={`ve-card p-5 transition-all border-2 border-dashed ${
            dragActive
              ? 'border-[#8B3FC5] bg-[#FFF8E5] dark:bg-[#3a2f0a]/40 scale-[1.01]'
              : 'border-slate-200 dark:border-white/15 hover:border-[#8B3FC5]/60'
          }`}
        >
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 dark:text-white/55">
            Upload — drag & drop, or click choose
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <select
              value={pickedKind}
              onChange={(e) => setPickedKind(e.target.value)}
              className="h-9 text-sm bg-transparent border border-slate-200 dark:border-white/15 rounded-md px-2 text-slate-700 dark:text-white/80"
              data-testid="docs-kind-select"
            >
              {Object.entries(KIND_LABELS).map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </select>
            <label className="ve-btn-primary cursor-pointer inline-flex items-center gap-2">
              <Upload size={14}/> Choose file{queue.length > 0 ? 's' : '(s)'}
              <input
                type="file"
                accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx,.csv,.txt"
                multiple
                onChange={(e) => uploadMany(e.target.files)}
                disabled={busy}
                className="hidden"
                data-testid="docs-upload-input"
              />
            </label>
            <span className="text-[10px] text-slate-400 dark:text-white/35">
              PDF · PNG · JPG · DOCX · XLSX — 50 MB each · 3 parallel
            </span>
          </div>
          {dragActive && (
            <div className="mt-3 text-sm text-[#7A5A00] dark:text-[#8B3FC5] font-bold inline-flex items-center gap-2">
              <Upload size={14}/> Drop to upload to the {KIND_LABELS[pickedKind]} bucket
            </div>
          )}
          {!dragActive && queue.length === 0 && (
            <div className="mt-3 text-xs text-slate-500 dark:text-white/55">
              Drop multiple files anywhere on this card. Each will be uploaded in parallel and OCR&rsquo;d automatically.
            </div>
          )}
          {/* Per-file progress queue */}
          {queue.length > 0 && (
            <ul className="mt-4 space-y-1.5" data-testid="docs-upload-queue">
              {queue.map((it) => (
                <li
                  key={it.id}
                  className="flex items-center gap-2 text-xs"
                  data-testid={`docs-queue-item-${it.status}`}
                >
                  <span className="w-3 h-3 shrink-0">
                    {it.status === 'queued' && <Clock size={12} className="text-slate-400"/>}
                    {it.status === 'uploading' && <Loader2 size={12} className="text-[#5E1F8B] animate-spin"/>}
                    {it.status === 'done' && <CheckCircle2 size={12} className="text-emerald-500"/>}
                    {it.status === 'failed' && <AlertTriangle size={12} className="text-rose-500"/>}
                  </span>
                  <span className="flex-1 truncate text-slate-700 dark:text-white/80">{it.name}</span>
                  <span className="text-[10px] text-slate-400 dark:text-white/40 uppercase tracking-[0.18em]">
                    {it.status === 'failed' ? (it.error || 'failed') : it.status}
                  </span>
                  {it.status !== 'uploading' && (
                    <button
                      onClick={() => setQueue((q) => q.filter((x) => x.id !== it.id))}
                      className="p-0.5 text-slate-400 hover:text-rose-500"
                      title="Dismiss"
                    >
                      <X size={11}/>
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="ve-card p-5">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 dark:text-white/55">Filter</div>
          <div className="mt-3 flex items-center gap-2">
            <div className="relative">
              <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400"/>
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search name, number, authority…"
                className="h-9 pl-7 text-sm bg-transparent border border-slate-200 dark:border-white/15 rounded-md pr-3 w-64 text-slate-700 dark:text-white/80"
                data-testid="docs-search"
              />
            </div>
            <select
              value={kindFilter}
              onChange={(e) => setKindFilter(e.target.value)}
              className="h-9 text-sm bg-transparent border border-slate-200 dark:border-white/15 rounded-md px-2 text-slate-700 dark:text-white/80"
              data-testid="docs-kind-filter"
            >
              <option value="all">All kinds</option>
              {Object.entries(KIND_LABELS).map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="mt-8 ve-card overflow-hidden">
        <table className="w-full text-sm" data-testid="docs-table">
          <thead className="bg-slate-50 dark:bg-white/[0.03] text-[10px] uppercase tracking-[0.18em] text-slate-500 dark:text-white/55">
            <tr>
              <th className="text-left px-4 py-3">Document</th>
              <th className="text-left px-4 py-3">Kind</th>
              <th className="text-left px-4 py-3">OCR</th>
              <th className="text-left px-4 py-3">Expiry</th>
              <th className="text-left px-4 py-3">Version</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} className="text-center py-12 text-slate-400">
                <Loader2 size={16} className="inline animate-spin mr-2"/>Loading documents…
              </td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={6} className="text-center py-12 text-slate-400 dark:text-white/40">
                No documents yet. Upload above to begin.
              </td></tr>
            )}
            {!loading && filtered.map((d) => (
              <tr key={d.document_id} className="border-t border-slate-100 dark:border-white/10 hover:bg-slate-50/60 dark:hover:bg-white/[0.02]">
                <td className="px-4 py-3">
                  <div className="font-medium text-slate-900 dark:text-white truncate max-w-xs" title={d.original_filename}>
                    {d.original_filename}
                  </div>
                  <div className="text-[10px] text-slate-400 dark:text-white/45 mt-0.5">
                    {d.doc_number_extracted ? `${d.doc_number_extracted} · ` : ''}
                    {d.issuing_authority_extracted || 'No authority detected'}
                  </div>
                </td>
                <td className="px-4 py-3 text-slate-700 dark:text-white/75 text-xs">
                  {KIND_LABELS[d.kind] || d.kind}
                </td>
                <td className="px-4 py-3">
                  <OcrStatusChip status={d.ocr_status} confidence={d.ocr_confidence}/>
                </td>
                <td className="px-4 py-3">
                  <ExpiryChip iso={d.expiry_date || d.expiry_date_extracted}/>
                </td>
                <td className="px-4 py-3 text-xs text-slate-600 dark:text-white/65">v{d.version || 1}</td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex items-center gap-1.5">
                    <button
                      onClick={() => viewDoc(d.document_id, 'view')}
                      className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#5E1F8B] dark:text-[#8B3FC5] hover:text-[#8B3FC5] inline-flex items-center gap-1"
                      data-testid={`docs-view-${d.document_id}`}
                      title="Open in a new tab"
                    >
                      <Eye size={11}/> View
                    </button>
                    <button
                      onClick={() => viewDoc(d.document_id, 'download')}
                      className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 dark:text-white/55 hover:text-[#8B3FC5] inline-flex items-center gap-1"
                      data-testid={`docs-download-${d.document_id}`}
                      title="Download to disk"
                    >
                      <Download size={11}/>
                    </button>
                    <label className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 dark:text-white/55 hover:text-[#5E1F8B] dark:hover:text-[#8B3FC5] cursor-pointer">
                      Replace
                      <input
                        type="file"
                        accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx,.csv,.txt"
                        onChange={(e) => replace(d.document_id, e.target.files?.[0])}
                        className="hidden"
                        data-testid={`docs-replace-${d.document_id}`}
                      />
                    </label>
                    <button
                      onClick={() => showVersions(d.document_id)}
                      className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 dark:text-white/55 hover:text-[#5E1F8B] dark:hover:text-[#8B3FC5] inline-flex items-center gap-1"
                      data-testid={`docs-versions-${d.document_id}`}
                    >
                      <History size={11}/> Versions
                    </button>
                    {d.ocr_status !== 'done' && d.ocr_status !== 'skipped' && (
                      <button
                        onClick={() => retryOcr(d.document_id)}
                        className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#5E1F8B] dark:text-[#8B3FC5] inline-flex items-center gap-1"
                        data-testid={`docs-retry-${d.document_id}`}
                        title="Re-run Gemini Vision OCR on this document"
                      >
                        <RefreshCcw size={11}/> {d.ocr_status === 'failed' ? 'Retry OCR' : 'Run OCR'}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Versions modal */}
      {versionsModal && (
        <div
          className="fixed inset-0 z-[70] bg-black/50 backdrop-blur-sm flex items-center justify-center p-6"
          onClick={(e) => { if (e.target === e.currentTarget) setVersionsModal(null); }}
        >
          <div className="ve-card max-w-2xl w-full p-6 max-h-[80vh] overflow-y-auto" data-testid="docs-versions-modal">
            <div className="flex items-start justify-between">
              <div>
                <div className="ve-eyebrow"><History size={11}/> Version chain</div>
                <h2 className="display text-xl font-bold mt-2 text-slate-900 dark:text-white">
                  {versionsModal.versions.length} version{versionsModal.versions.length === 1 ? '' : 's'}
                </h2>
              </div>
              <button onClick={() => setVersionsModal(null)} className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-white/10">×</button>
            </div>
            <div className="mt-5 space-y-3">
              {versionsModal.versions.map((v) => (
                <div
                  key={v.document_id}
                  className={`p-3 rounded-md border ${v.is_current_version
                    ? 'border-[#8B3FC5] bg-[#FFF8E5]/40 dark:bg-[#3a2f0a]/30'
                    : 'border-slate-200 dark:border-white/10'}`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-medium text-sm truncate text-slate-900 dark:text-white">
                        v{v.version || 1} · {v.original_filename}
                      </div>
                      <div className="text-[10px] text-slate-500 dark:text-white/55 mt-0.5">
                        {new Date(v.created_at).toLocaleString()}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <ExpiryChip iso={v.expiry_date || v.expiry_date_extracted}/>
                      <OcrStatusChip status={v.ocr_status} confidence={v.ocr_confidence}/>
                      {v.is_current_version && (
                        <span className="text-[9px] uppercase tracking-[0.22em] font-bold text-[#5E1F8B] dark:text-[#8B3FC5]">Current</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
